import { useState } from 'react';
import useProjectStore from '../stores/projectStore';
import { useAgentBoard, useAgentIntent } from '../hooks/useAgentLibrary';

// State-Machine → Farbe/Label (semantisch, im Club-Ton gehalten)
const STATUS = {
  not_configured:    { label: 'Nicht konfiguriert', color: '#6b7280' },
  waiting:           { label: 'Wartet',             color: '#b8860b' },
  ready:             { label: 'Bereit',             color: '#0C9488' },
  running:           { label: 'Läuft',              color: '#2563eb' },
  finished:          { label: 'Fertig',             color: '#2d7d6f' },
  failed:            { label: 'Fehler',             color: '#dc2626' },
  waiting_for_input: { label: 'Wartet auf Eingabe', color: '#7c4dba' },
};

function fmt(dt) {
  if (!dt) return '–';
  try {
    return new Date(dt).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '–';
  }
}

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.not_configured;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ color: s.color, backgroundColor: s.color + '18' }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
      {s.label}
    </span>
  );
}

export default function CustomerAgents() {
  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  const projects = useProjectStore((s) => s.projects);
  const current = projects.find((p) => p.id === currentProjectId);

  const { data, isLoading, isFetching, refetch } = useAgentBoard(currentProjectId);
  const intent = useAgentIntent();
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const agents = data?.agents || [];

  const runAgent = async (agent) => {
    setError(null);
    setBusyId(agent.id);
    try {
      await intent.mutateAsync({ intent: 'agent.run', params: { role: agent.role } });
      await refetch();
    } catch (e) {
      const d = e.response?.data;
      setError({ id: agent.id, msg: d?.blocked_reason || d?.error || e.message });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight text-[#0D3B50]">Agenten</h1>
          <p className="text-[13.5px] text-gray-500 mt-1">
            {current?.name ? <>Über-Agent von <span className="font-medium text-gray-700">{current.name}</span> — Status, Voraussetzungen und Läufe.</> : 'Kein Projekt ausgewählt.'}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-[13px] px-3.5 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
        >
          {isFetching ? 'Aktualisiere…' : 'Status aktualisieren'}
        </button>
      </div>

      {isLoading ? (
        <div className="text-[13px] text-gray-500 p-6">Lade…</div>
      ) : agents.length === 0 ? (
        <div className="bg-white rounded-xl border border-black/[.07] p-8 text-center">
          <div className="text-[14px] text-gray-700 font-medium mb-1">Noch keine Agenten</div>
          <div className="text-[12.5px] text-gray-500">
            Für dieses Projekt ist noch kein Agent aktiviert.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {agents.map((a) => (
            <AgentStatusCard
              key={a.id}
              agent={a}
              busy={busyId === a.id}
              error={error?.id === a.id ? error.msg : null}
              onRun={() => runAgent(a)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AgentStatusCard({ agent, busy, error, onRun }) {
  const deps = (agent.dependencies || []).flatMap((g) => g.deps || []);
  const out = agent.last_run?.output;
  const recCount = Array.isArray(out?.recommendations) ? out.recommendations.length : 0;
  const canRun = !busy && agent.status !== 'running';

  return (
    <div className="bg-white rounded-2xl border border-black/[.07] shadow-sm p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-[15px] text-[#111f30] leading-tight truncate">
            {agent.display_name}
          </h3>
          {agent.template?.description && (
            <p className="text-[12px] text-gray-500 mt-1 line-clamp-2">{agent.template.description}</p>
          )}
        </div>
        <StatusBadge status={agent.status} />
      </div>

      {agent.blocked_reason && (
        <div className="mt-3 text-[12px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          {agent.blocked_reason}
        </div>
      )}
      {agent.status === 'failed' && agent.last_error && (
        <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {agent.last_error}
        </div>
      )}

      {/* Voraussetzungen */}
      {deps.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-400 font-medium mb-1.5">Voraussetzungen</div>
          <ul className="space-y-1">
            {deps.map((d, i) => (
              <li key={i} className="flex items-center gap-2 text-[12.5px]">
                <span style={{ color: d.satisfied ? '#2d7d6f' : '#dc2626' }}>{d.satisfied ? '✓' : '✗'}</span>
                <span className={d.satisfied ? 'text-gray-700' : 'text-gray-500'}>{d.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Letzter Lauf / Report */}
      {agent.last_run && (
        <div className="mt-3 bg-gray-50 rounded-lg px-3 py-2.5">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[12px] font-medium text-gray-700 truncate">
              {out?.report?.title || 'Letztes Ergebnis'}
            </div>
            {out?.generated_placeholder ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">Platzhalter</span>
            ) : out?.source === 'ga4' ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 shrink-0">GA4 live</span>
            ) : null}
          </div>
          {out?.report?.period && <div className="text-[11px] text-gray-400 mt-0.5">{out.report.period}</div>}

          {out?.report && <ReportPanel output={out} />}

          <div className="text-[11.5px] text-gray-500 mt-2">
            {recCount} Empfehlung{recCount === 1 ? '' : 'en'}
          </div>
        </div>
      )}

      {/* Aufgaben */}
      {agent.tasks?.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-400 font-medium mb-1.5">
            Aufgaben ({agent.open_tasks})
          </div>
          <ul className="space-y-1.5">
            {agent.tasks.map((t) => (
              <li key={t.id} className="flex items-start gap-2 text-[12px]">
                <span className="mt-px text-[9px] px-1.5 py-0.5 rounded shrink-0 font-medium uppercase" style={impactStyle(t.impact)}>
                  {t.impact}
                </span>
                <span className="flex-1 text-gray-700 leading-snug">{t.instruction}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
        <div className="text-[11.5px] text-gray-500 leading-tight">
          <div>Letzter Lauf: {fmt(agent.last_run_at)}</div>
          <div>Nächster Lauf: {fmt(agent.next_run_at)}</div>
        </div>
        <button
          onClick={onRun}
          disabled={!canRun}
          className="text-[12.5px] px-3.5 py-2 bg-[#0D3B50] text-white rounded-lg hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
        >
          {busy ? 'Läuft…' : 'Jetzt ausführen'}
        </button>
      </div>

      {error && <div className="mt-2 text-[11.5px] text-red-600">{error}</div>}
    </div>
  );
}

// ─── Report-Panel (Agent-spezifisch: GA4 / GSC) ─────────────────────────────
function fmtNum(n) {
  if (n === null || n === undefined) return '–';
  return Number(n).toLocaleString('de-DE');
}

function impactStyle(impact) {
  const c = impact === 'hoch' ? '#dc2626' : impact === 'mittel' ? '#b8860b' : '#6b7280';
  return { color: c, backgroundColor: c + '18' };
}

const COUNT_LABELS = {
  erfuellt: 'erfüllt', handlungsbedarf: 'Handlungsbedarf', delegiert: 'delegiert',
  unklar: 'unklar', ok: 'ok', warn: 'Warnung', fail: 'Fehler', pending: 'offen (LLM)',
};

function ReportPanel({ output }) {
  const r = output.report || {};
  if (output.source === 'gsc') return <SeoBody r={r} />;
  if (output.source === 'ga4') return <Ga4Body r={r} />;
  return <SubAgentBody r={r} />;
}

// Generische Sicht für die SEO-Sub-Agenten (OnPage/Technik/Content/EEAT) + Reporting.
function SubAgentBody({ r }) {
  const counts = r.counts || {};
  const entries = Object.entries(counts).filter(([k]) => !['total', 'auto'].includes(k));
  return (
    <div className="mt-2.5 space-y-2">
      {r.score != null && (
        <div className="inline-block bg-white rounded-md border border-gray-100 px-2.5 py-1.5 text-center">
          <div className="text-[15px] font-semibold text-[#111f30] tabular-nums leading-none">{r.score}</div>
          <div className="text-[9.5px] uppercase tracking-wider text-gray-400 mt-1">Score</div>
        </div>
      )}
      {entries.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {entries.map(([k, v]) => (
            <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
              {(COUNT_LABELS[k] || k)}: <span className="tabular-nums font-medium text-gray-800">{v}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function OnpageBody({ r }) {
  const m = r.metrics || {};
  const c = r.counts || {};
  return (
    <div className="mt-2.5 space-y-2">
      <KpiGrid items={[
        ['Score', `${m.score ?? 0}`],
        ['Fehler', fmtNum(m.fails)],
        ['Warnungen', fmtNum(m.warnings)],
        ['Aufgaben', fmtNum(m.tasks)],
      ]} />
      <div className="text-[11px] text-gray-500">
        {(c.ok || 0)} ok · {(c.warn || 0)} Warnung · {(c.fail || 0)} Fehler · {(c.pending || 0)} offen (LLM)
      </div>
    </div>
  );
}

function Ga4Body({ r }) {
  const m = r.metrics || {};
  const channels = (r.top_channels || []).slice(0, 4).map((c) => ({ key: c.key, value: c.sessions }));
  const devices = (r.devices || []).map((d) => ({ key: d.key, value: d.sessions }));
  const trend = (r.daily_trend || []).map((d) => d.sessions || 0);
  return (
    <div className="mt-2.5 space-y-3">
      <KpiGrid items={[
        ['Sitzungen', fmtNum(m.sessions)],
        ['Nutzer', fmtNum(m.users)],
        ['Conv.', fmtNum(m.conversions)],
        ['Engmt.', `${m.engagement_rate ?? 0}%`],
      ]} />
      <Trend values={trend} label="Sitzungen · Trend (30 T.)" />
      <BarList title="Top-Kanäle" items={channels} />
      {devices.length > 0 && <Split items={devices} />}
    </div>
  );
}

function SeoBody({ r }) {
  const m = r.metrics || {};
  const queries = (r.top_queries || []).slice(0, 5);
  const opps = (r.opportunities || []).slice(0, 4);
  const trend = (r.daily_trend || []).map((d) => d.clicks || 0);
  return (
    <div className="mt-2.5 space-y-3">
      <KpiGrid items={[
        ['Klicks', fmtNum(m.clicks)],
        ['Impr.', fmtNum(m.impressions)],
        ['CTR', `${m.ctr ?? 0}%`],
        ['Ø Pos.', m.position ?? '–'],
      ]} />
      <Trend values={trend} label="Klicks · Trend (28 T.)" />
      <BarList title="Top-Queries" items={queries} />
      {opps.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Near-Miss-Chancen (Pos 4–15)</div>
          <ul className="space-y-1">
            {opps.map((o, i) => (
              <li key={i} className="flex items-center gap-2 text-[11.5px]">
                <span className="flex-1 truncate text-gray-600">{o.query}</span>
                <span className="text-gray-400">Pos {o.position}</span>
                <span className="w-16 text-right text-gray-500 tabular-nums">
                  {o.volume ? `Vol ${fmtNum(o.volume)}` : `${fmtNum(o.impressions)} Impr`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {r.xovi?.configured && r.xovi.keyword_count > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
            XOVI-Abgleich · {r.xovi.keyword_count} Keywords{r.xovi.matched ? `, ${r.xovi.matched} im Abgleich` : ' · keine Überschneidung'}
          </div>
          <ul className="space-y-1">
            {r.xovi.reconciliation?.length > 0
              ? r.xovi.reconciliation.slice(0, 4).map((x, i) => (
                  <li key={i} className="flex items-center gap-2 text-[11.5px]">
                    <span className="flex-1 truncate text-gray-600">{x.keyword}</span>
                    <span className="text-gray-400">GSC {x.gsc_position} · XOVI {x.xovi_position ?? '—'}</span>
                    <span className="w-14 text-right text-gray-500 tabular-nums">Vol {fmtNum(x.volume)}</span>
                  </li>
                ))
              : (r.xovi.top_keywords || []).slice(0, 4).map((x, i) => (
                  <li key={i} className="flex items-center gap-2 text-[11.5px]">
                    <span className="flex-1 truncate text-gray-600">{x.key}</span>
                    <span className="text-gray-400">{x.position ? `Pos ${x.position}` : 'rankt nicht'}</span>
                    <span className="w-14 text-right text-gray-500 tabular-nums">Vol {fmtNum(x.volume)}</span>
                  </li>
                ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function KpiGrid({ items }) {
  return (
    <div className="grid grid-cols-4 gap-2">
      {items.map(([label, value], i) => (
        <div key={i} className="bg-white rounded-md border border-gray-100 px-2 py-1.5 text-center">
          <div className="text-[14px] font-semibold text-[#111f30] tabular-nums leading-none">{value}</div>
          <div className="text-[9.5px] uppercase tracking-wider text-gray-400 mt-1">{label}</div>
        </div>
      ))}
    </div>
  );
}

function BarList({ title, items }) {
  if (!items || items.length === 0) return null;
  const max = Math.max(1, ...items.map((it) => it.value || 0));
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{title}</div>
      <div className="space-y-1">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 text-[11.5px]">
            <span className="w-24 shrink-0 truncate text-gray-600">{it.key}</span>
            <span className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
              <span className="block h-full rounded-full" style={{ width: `${((it.value || 0) / max) * 100}%`, backgroundColor: '#0D3B50' }} />
            </span>
            <span className="w-10 text-right text-gray-500 tabular-nums">{fmtNum(it.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Split({ items }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500">
      {items.map((d, i) => (
        <span key={i}>{d.key}: <span className="text-gray-700 font-medium">{fmtNum(d.value)}</span></span>
      ))}
    </div>
  );
}

function Trend({ values, label }) {
  if (!values || values.length < 2) return null;
  const w = 120, h = 30;
  const max = Math.max(...values), min = Math.min(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">{label}</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-7" preserveAspectRatio="none">
        <polyline points={pts} fill="none" stroke="#0D3B50" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}
